// Intent: account state for the P0->P1 transition (guest -> phone+SMS account).
// The app stays fully usable as a guest; this store only ADDS an account on top —
// no screen may ever require login to reach guest features (privacy decision).
// Persists the account snapshot in kv and the refresh token via token-storage;
// the short-lived access token lives in memory only.
//
// NOTE (React Compiler + zustand): no store getters for derived values — derive
// with pure functions over subscribed state (see goals-store for the full story).

import { create } from 'zustand';

import {
  AuthPayload,
  googleLogin,
  MaskedMatch,
  MatchStatus,
  MobileAccount,
  pollEmailLogin,
  requestEmailLogin,
  requestOtp,
  verifyEmailCode,
  verifyOtp,
} from '@/api/auth-api';
import { getDeviceId } from '@/api/device-id';
import {
  confirmLinkApi,
  fetchMeApi,
  linkByPeselApi,
  linkHelpApi,
  LinkHelpInput,
  requestAccountPhoneApi,
  updateConsentsApi,
  verifyAccountPhoneApi,
} from '@/api/mobile-api';
import { clearRefreshToken, saveRefreshToken } from '@/api/token-storage';
import { getDb } from '@/db';

const KV_AUTH_ACCOUNT = 'auth_account';

// 1.2.0: onboarding may collect the marketing consent BEFORE any account exists
// (checkbox next to the optional e-mail field). Kept module-side and pushed to
// the server right after the first session lands — see applyAuthPayload.
let pendingMarketingConsent = false;

export function setPendingMarketingConsent(value: boolean): void {
  pendingMarketingConsent = value;
}

// 1.3.0: result of the automatic household join (onboarding "Mam zaproszenie",
// code persisted in app-meta). Consumed once by Start to show the toast — the
// join itself runs in applyAuthPayload where the fresh session lands.
let householdJoinResult: { ok: boolean; name: string | null } | null = null;

export function consumeHouseholdJoinResult(): { ok: boolean; name: string | null } | null {
  const r = householdJoinResult;
  householdJoinResult = null;
  return r;
}

interface PersistedAuth {
  account: MobileAccount;
  matchStatus: MatchStatus;
  maskedMatch: MaskedMatch | null;
  /** Agent-mode tab flag (presentation-only; server re-checks every call). */
  isAgent?: boolean;
  /** Admin-mode flag (presentation-only; server re-checks every call). */
  isAdmin?: boolean;
}

interface AuthState {
  account: MobileAccount | null;
  matchStatus: MatchStatus | null;
  maskedMatch: MaskedMatch | null;
  /** True = show the AGENT tab (account phone matches a CRM agent user). */
  isAgent: boolean;
  /** True = AGENT tab in admin view: ALL chats (both websites + app). */
  isAdmin: boolean;
  /** In-memory only (15 min token) — re-obtained via refresh when needed. */
  accessToken: string | null;
  /** Phone the user is mid-login with (between the phone and code screens). */
  pendingPhone: string | null;
  /** A friend's referral code typed on the phone screen — sent with verify. */
  pendingReferralCode: string | null;
  /** E-mail mid-login (between the login hub and the "check your inbox" screen). */
  pendingEmail: string | null;
  /** Challenge id of the e-mail magic-link login the app is polling. */
  pendingChallengeId: string | null;
  /**
   * This session logged in without SMS while the account has a phone — cloud
   * restore stays locked until verifyPhone succeeds (server enforces it too).
   */
  requiresSmsVerify: boolean;

  hydrate: () => Promise<void>;
  /** Re-sync account/match from the server (linkStatus can change in the CRM). */
  refreshMe: () => Promise<void>;
  requestCode: (phone: string, referralCode?: string) => Promise<{ ok: boolean; message: string | null }>;
  verifyCode: (code: string, firstName?: string) => Promise<{ ok: boolean; message: string | null }>;
  /** E-mail magic-link login: send the mail, remember the challenge to poll. */
  startEmailLogin: (email: string, referralCode?: string) => Promise<{ ok: boolean; message: string | null }>;
  /** One poll tick; 'OK' means the session was applied. */
  pollEmailOnce: () => Promise<{ status: 'PENDING' | 'OK' | 'EXPIRED' | 'ERROR'; message: string | null }>;
  /** Fallback: type the 6-digit code from the mail instead of clicking. */
  submitEmailCode: (code: string, firstName?: string) => Promise<{ ok: boolean; message: string | null }>;
  /** Google Sign-In: the screen obtains the Google idToken, we exchange it. */
  loginWithGoogle: (idToken: string, firstName?: string) => Promise<{ ok: boolean; message: string | null }>;
  /** SMS to a new phone (attach) or the account's phone (restore 2FA). */
  requestPhoneCode: (phone?: string) => Promise<{ ok: boolean; message: string | null }>;
  /** Verify the SMS code; swaps in fresh SMS-verified tokens on success. */
  verifyPhoneCode: (code: string, phone?: string) => Promise<{ ok: boolean; message: string | null }>;
  /** Persist consent switches server-side (onboarding checkbox, privacy screen). */
  saveConsents: (consents: { marketingConsent?: boolean; analyticsConsent?: boolean }) => Promise<void>;
  /** Used by mobile-api's silent 401-refresh — stores the rotated session. */
  applySession: (payload: AuthPayload) => Promise<void>;
  confirmLink: () => Promise<{ ok: boolean; message: string | null }>;
  linkByPesel: (pesel: string) => Promise<{ ok: boolean; message: string | null }>;
  requestLinkHelp: (input: LinkHelpInput) => Promise<{ ok: boolean; message: string | null }>;
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  account: null,
  matchStatus: null,
  maskedMatch: null,
  isAgent: false,
  isAdmin: false,
  accessToken: null,
  pendingPhone: null,
  pendingReferralCode: null,
  pendingEmail: null,
  pendingChallengeId: null,
  requiresSmsVerify: false,

  hydrate: async () => {
    const db = await getDb();
    const raw = await db.getKv(KV_AUTH_ACCOUNT);
    if (!raw) return;
    try {
      const persisted = JSON.parse(raw) as PersistedAuth;
      set({
        account: persisted.account,
        matchStatus: persisted.matchStatus,
        maskedMatch: persisted.maskedMatch,
        isAgent: persisted.isAgent === true,
        isAdmin: persisted.isAdmin === true,
      });
      // Fire-and-forget: linkStatus can change server-side (office links the
      // account in the CRM) — sync the snapshot without blocking startup.
      void get().refreshMe();
    } catch {
      // Corrupted snapshot -> stay guest; user can just log in again.
    }
  },

  refreshMe: async () => {
    const result = await fetchMeApi();
    if (!result.ok || !result.data) return;
    const db = await getDb();
    const persisted: PersistedAuth = {
      account: result.data.account,
      matchStatus: result.data.matchStatus,
      maskedMatch: result.data.maskedMatch,
      isAgent: result.data.isAgent === true,
      isAdmin: result.data.isAdmin === true,
    };
    await db.setKv(KV_AUTH_ACCOUNT, JSON.stringify(persisted));
    set({
      account: result.data.account,
      matchStatus: result.data.matchStatus,
      maskedMatch: result.data.maskedMatch,
      isAgent: result.data.isAgent === true,
      isAdmin: result.data.isAdmin === true,
    });
  },

  requestCode: async (phone, referralCode) => {
    const deviceId = await getDeviceId();
    const result = await requestOtp(phone, deviceId);
    if (result.ok) {
      set({ pendingPhone: phone, pendingReferralCode: referralCode?.trim() || null });
    }
    return { ok: result.ok, message: result.message };
  },

  verifyCode: async (code, firstName) => {
    const phone = get().pendingPhone;
    if (!phone) {
      return { ok: false, message: 'Zacznij od podania numeru telefonu.' };
    }
    const deviceId = await getDeviceId();
    const result = await verifyOtp(phone, code, deviceId, firstName, get().pendingReferralCode ?? undefined);
    if (!result.ok || !result.data) {
      return { ok: result.ok, message: result.message };
    }
    await applyAuthPayload(result.data, set);
    return { ok: true, message: result.message };
  },

  applySession: async (payload) => {
    await applyAuthPayload(payload, set);
  },

  startEmailLogin: async (email, referralCode) => {
    const deviceId = await getDeviceId();
    const result = await requestEmailLogin(email.trim().toLowerCase(), deviceId);
    if (result.ok && result.data) {
      set({
        pendingEmail: email.trim().toLowerCase(),
        pendingChallengeId: result.data.challengeId,
        pendingReferralCode: referralCode?.trim() || null,
      });
    }
    return { ok: result.ok, message: result.message };
  },

  pollEmailOnce: async () => {
    const challengeId = get().pendingChallengeId;
    if (!challengeId) return { status: 'ERROR', message: 'Zacznij od podania adresu e-mail.' };
    const deviceId = await getDeviceId();
    const result = await pollEmailLogin(challengeId, deviceId);
    if (!result.ok || !result.data) {
      return { status: 'ERROR', message: result.message };
    }
    if (result.data.status === 'OK' && result.data.auth) {
      await applyAuthPayload(result.data.auth, set);
      return { status: 'OK', message: result.message };
    }
    if (result.data.status === 'EXPIRED') {
      set({ pendingChallengeId: null });
      return { status: 'EXPIRED', message: null };
    }
    return { status: 'PENDING', message: null };
  },

  submitEmailCode: async (code, firstName) => {
    const challengeId = get().pendingChallengeId;
    if (!challengeId) return { ok: false, message: 'Zacznij od podania adresu e-mail.' };
    const deviceId = await getDeviceId();
    const result = await verifyEmailCode(
      challengeId,
      code,
      deviceId,
      firstName,
      get().pendingReferralCode ?? undefined,
    );
    if (!result.ok || !result.data) {
      return { ok: result.ok, message: result.message };
    }
    await applyAuthPayload(result.data, set);
    return { ok: true, message: result.message };
  },

  loginWithGoogle: async (idToken, firstName) => {
    const deviceId = await getDeviceId();
    const result = await googleLogin(idToken, deviceId, firstName, get().pendingReferralCode ?? undefined);
    if (!result.ok || !result.data) {
      return { ok: result.ok, message: result.message };
    }
    await applyAuthPayload(result.data, set);
    return { ok: true, message: result.message };
  },

  requestPhoneCode: async (phone) => {
    const result = await requestAccountPhoneApi(phone);
    return { ok: result.ok, message: result.message };
  },

  verifyPhoneCode: async (code, phone) => {
    const result = await verifyAccountPhoneApi(code, phone);
    if (!result.ok || !result.data) {
      return { ok: result.ok, message: result.message };
    }
    // Fresh SMS-verified tokens: swapping them in is what unlocks cloud restore.
    await applyAuthPayload(result.data, set);
    return { ok: true, message: result.message };
  },

  saveConsents: async (consents) => {
    // Best effort — a failed consent save must never block onboarding/login.
    try {
      await updateConsentsApi(consents);
    } catch {
      // ignored
    }
  },

  confirmLink: async () => {
    const result = await confirmLinkApi();
    if (result.ok) {
      await markLinked(set, get);
    }
    return { ok: result.ok, message: result.message };
  },

  linkByPesel: async (pesel) => {
    const result = await linkByPeselApi(pesel);
    if (result.ok) {
      await markLinked(set, get);
    }
    return { ok: result.ok, message: result.message };
  },

  requestLinkHelp: async (input) => {
    const result = await linkHelpApi(input);
    if (result.ok) {
      await persistLinkState(set, get, 'HELP_REQUESTED', get().matchStatus, get().maskedMatch);
    }
    return { ok: result.ok, message: result.message };
  },

  logout: async () => {
    // Local logout only: drops the session on this device. Server-side revocation
    // of the refresh-token family is a P2 endpoint (not built yet).
    const db = await getDb();
    await db.setKv(KV_AUTH_ACCOUNT, '');
    await clearRefreshToken();
    set({
      account: null,
      matchStatus: null,
      maskedMatch: null,
      isAgent: false,
      isAdmin: false,
      accessToken: null,
      pendingPhone: null,
      pendingReferralCode: null,
      pendingEmail: null,
      pendingChallengeId: null,
      requiresSmsVerify: false,
    });
  },
}));

/** Link succeeded: account is LINKED from now on (both fields drive UI states). */
async function markLinked(
  set: (partial: Partial<AuthState>) => void,
  get: () => AuthState,
): Promise<void> {
  await persistLinkState(set, get, 'LINKED', 'LINKED', null);
}

async function persistLinkState(
  set: (partial: Partial<AuthState>) => void,
  get: () => AuthState,
  linkStatus: string,
  matchStatus: MatchStatus | null,
  maskedMatch: MaskedMatch | null,
): Promise<void> {
  const account = get().account;
  if (!account) return;
  const updated: MobileAccount = { ...account, linkStatus };
  const db = await getDb();
  const persisted: PersistedAuth = {
    account: updated,
    matchStatus: matchStatus ?? 'NONE',
    maskedMatch,
    isAgent: get().isAgent,
    isAdmin: get().isAdmin,
  };
  await db.setKv(KV_AUTH_ACCOUNT, JSON.stringify(persisted));
  set({ account: updated, matchStatus, maskedMatch });
}

async function applyAuthPayload(
  payload: AuthPayload,
  set: (partial: Partial<AuthState>) => void,
): Promise<void> {
  const db = await getDb();
  // A real login must never land on leftover DEMO data. Demo seed (KV_SEEDED)
  // persists from any earlier dev-mode run and would otherwise show the new user
  // a fake household — the president hit exactly this after Google login
  // (2026-07-18). Gated on the demo-only flag, so real guest data is untouched.
  const { clearDemoDataIfPresent } = await import('@/db/seed');
  await clearDemoDataIfPresent(db);
  // isAgent/isAdmin may be absent on older/mock refresh payloads — absent means
  // "keep whatever we knew", never silently downgrade a session mid-flight.
  const isAgent =
    payload.isAgent === undefined ? useAuthStore.getState().isAgent : payload.isAgent === true;
  const isAdmin =
    payload.isAdmin === undefined ? useAuthStore.getState().isAdmin : payload.isAdmin === true;
  const persisted: PersistedAuth = {
    account: payload.account,
    matchStatus: payload.matchStatus,
    maskedMatch: payload.maskedMatch,
    isAgent,
    isAdmin,
  };
  await db.setKv(KV_AUTH_ACCOUNT, JSON.stringify(persisted));
  await saveRefreshToken(payload.refreshToken);
  set({
    account: payload.account,
    matchStatus: payload.matchStatus,
    maskedMatch: payload.maskedMatch,
    isAgent,
    isAdmin,
    accessToken: payload.accessToken,
    pendingPhone: null,
    pendingReferralCode: null,
    pendingEmail: null,
    pendingChallengeId: null,
    requiresSmsVerify: payload.requiresSmsVerify === true,
  });
  if (pendingMarketingConsent) {
    // Consent ticked in onboarding, account just materialized — persist it.
    pendingMarketingConsent = false;
    void updateConsentsApi({ marketingConsent: true });
  }
  // Cloud backup turns on by itself after login (unless the user explicitly
  // switched it off before) — logging in already promises "kopia w chmurze".
  void (async () => {
    const { enableCloudSyncAfterLogin } = await import('@/sync/cloud-sync');
    await enableCloudSyncAfterLogin();
  })().catch(() => undefined);
  // Invite code typed in onboarding — join now that a session exists. A bad
  // code only produces a message on Start; it never blocks the login flow.
  // Dynamic imports dodge the static cycle (household-sync imports this store).
  void (async () => {
    const { useAppMetaStore } = await import('@/store/app-meta-store');
    const code = useAppMetaStore.getState().pendingHouseholdCode;
    if (!code) return;
    await useAppMetaStore.getState().setPendingHouseholdCode(null);
    const { joinHouseholdApi } = await import('@/api/mobile-api');
    const res = await joinHouseholdApi(code);
    if (res.ok && res.data) {
      const sync = await import('@/sync/household-sync');
      sync.setHouseholdActive(res.data.id);
      void sync.syncHouseholdNow().catch(() => undefined);
      householdJoinResult = { ok: true, name: res.data.name ?? null };
    } else {
      householdJoinResult = { ok: false, name: null };
    }
  })().catch(() => undefined);
}
